/**
 * Getty Favorites API utilities
 *
 * Provides functions for managing user's Getty vocabulary favorites.
 */

import api from './api';

export type VocabularyType =
  | 'work_type'
  | 'techniques'
  | 'materials'
  | 'substrate'
  | 'signature_type'
  | 'signature_location'
  | 'title_location'
  | 'date_location';

export interface GettyFavoriteItem {
  uri: string;
  textValue: string;
  breadcrumbs: string[];
}

export interface GettyFavoritesResponse {
  workType: GettyFavoriteItem[];
  techniques: GettyFavoriteItem[];
  materials: GettyFavoriteItem[];
  substrate: GettyFavoriteItem[];
  signatureType: GettyFavoriteItem[];
  signatureLocation: GettyFavoriteItem[];
  titleLocation: GettyFavoriteItem[];
  dateLocation: GettyFavoriteItem[];
  updatedAt: string | null;
}

/**
 * Get all Getty vocabulary favorites for the current user or a delegated catalog owner.
 *
 * When ownerId is provided, the backend will verify the active delegation and
 * return the catalog owner's favorites instead of the caller's own (fixes #952).
 */
export const getGettyFavorites = async (ownerId?: string): Promise<GettyFavoritesResponse> => {
  const params: Record<string, string> = {};
  if (ownerId) {
    params.owner_id = ownerId;
  }
  const response = await api.get('/api/getty-favorites', { params });
  return response.data;
};

/**
 * Add a Getty vocabulary favorite.
 */
export const addGettyFavorite = async (
  vocabularyType: VocabularyType,
  uri: string,
  textValue: string,
  breadcrumbs: string[],
  ownerId?: string
): Promise<GettyFavoritesResponse> => {
  const response = await api.post(
    '/api/getty-favorites',
    { vocabularyType, uri, textValue, breadcrumbs },
    ownerId ? { params: { owner_id: ownerId } } : undefined
  );
  return response.data;
};

/**
 * Remove a Getty vocabulary favorite.
 */
export const removeGettyFavorite = async (
  vocabularyType: VocabularyType,
  uri: string,
  ownerId?: string
): Promise<GettyFavoritesResponse> => {
  const params: Record<string, string> = { vocabulary_type: vocabularyType, uri };
  if (ownerId) {
    params.owner_id = ownerId;
  }
  const response = await api.delete('/api/getty-favorites', { params });
  return response.data;
};
